import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, Input, List, StatusTip, Tag } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import type { IAuthorizedUser } from '@op/services/report';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';

interface IPermissionUserInputProps {
  value: IAuthorizedUser[];
  onChange: (users: IAuthorizedUser[]) => void;
  onSearch: (keyword: string) => void;
  searchOptions: IAuthorizedUser[];
  searchLoading?: boolean;
  hasMore?: boolean;
  loadMoreLoading?: boolean;
  onLoadMore?: () => void;
  isError?: boolean;
  onClearError?: () => void;
}

interface IPermissionUserOptionProps {
  item: IAuthorizedUser;
  onSelect: () => void;
}

function PermissionUserOption({ item, onSelect }: IPermissionUserOptionProps) {
  return (
    <List.Item onClick={onSelect}>
      <div className="dbo-permission-user-input__option">
        <RenderImg
          coverImage={item.profile_pic || ''}
          defaultImage={DefaultImg.KOL}
          className="dbo-permission-user-input__option-avatar"
          mode="image"
        />
        <div className="dbo-permission-user-input__option-text">
          <div className="dbo-permission-user-input__option-name">
            {item.user_name || item.user_id}
          </div>
          {item.department_name && (
            <div className="dbo-permission-user-input__option-dept">
              {item.department_name}
            </div>
          )}
        </div>
      </div>
    </List.Item>
  );
}

export default function PermissionUserInput({
  value,
  onChange,
  onSearch,
  searchOptions,
  searchLoading = false,
  hasMore = false,
  loadMoreLoading = false,
  onLoadMore,
  isError = false,
  onClearError,
}: IPermissionUserInputProps) {
  const { formatMessage } = useLanguage();
  const [keyword, setKeyword] = useState('');

  const selectedIds = useMemo(
    () => new Set(value.map((item) => item.user_id)),
    [value],
  );

  const filteredOptions = useMemo(
    () => searchOptions.filter((item) => !selectedIds.has(item.user_id)),
    [searchOptions, selectedIds],
  );

  useEffect(() => {
    onSearch(keyword);
  }, [keyword]);

  const handleAddUser = useCallback(
    (user: IAuthorizedUser, close?: () => void) => {
      if (!user?.user_id || selectedIds.has(user.user_id)) return;
      onChange([...value, user]);
      setKeyword('');
      onClearError?.();
      close?.();
    },
    [onChange, onClearError, selectedIds, value],
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      onChange(value.filter((item) => item.user_id !== userId));
    },
    [onChange, value],
  );

  const handleKeywordChange = useCallback(
    (nextValue: string) => {
      setKeyword(nextValue);
      onClearError?.();
    },
    [onClearError],
  );

  const dropdownOpen = !!keyword.trim();

  const placeholderValue = useMemo(() => {
    return value.length > 0 || keyword
      ? ''
      : formatMessage({
        id: 'Enter an English name or email address to search, then select and add from the drop-down list.',
      });
  }, [formatMessage, keyword, value.length]);

  const renderDropdownContent = useCallback(
    (close: () => void) => {
      if (searchLoading) {
        return <StatusTip status="loading" />;
      }

      if (filteredOptions.length === 0) {
        return (
          <StatusTip
            status="empty"
            emptyText={formatMessage({ id: 'No data yet' })}
          />
        );
      }

      return (
        <>
          {filteredOptions.map((item) => (
            <PermissionUserOption
              key={item.user_id}
              item={item}
              onSelect={() => handleAddUser(item, close)}
            />
          ))}
          {loadMoreLoading && (
            <List.Item>
              <StatusTip status="loading" />
            </List.Item>
          )}
        </>
      );
    },
    [
      filteredOptions,
      formatMessage,
      handleAddUser,
      loadMoreLoading,
      searchLoading,
    ],
  );

  const handleScrollBottom = useCallback(() => {
    if (!hasMore || searchLoading || loadMoreLoading) return;
    onLoadMore?.();
  }, [hasMore, loadMoreLoading, onLoadMore, searchLoading]);

  return (
    <Dropdown
      clickClose={false}
      appearance="pure"
      matchButtonWidth
      open={dropdownOpen}
      className="dbo-permission-user-input__dropdown"
      button={
        <div className={`dbo-permission-user-input${isError ? ' dbo-permission-user-input--error' : ''}`} >
          {value.map((item) => (
            <Tag
              key={item.user_id}
              className="dbo-permission-user-input__tag tea-tag--unlimited-width"
              onClose={() => handleRemoveUser(item.user_id)}
              title={item.user_name || item.user_id}
            >
              {item.user_name || item.user_id}
            </Tag>
          ))}
          <Input
            className="dbo-permission-user-input__field"
            value={keyword}
            placeholder={placeholderValue}
            onChange={handleKeywordChange}
            onKeyDown={(event) => {
              if (event.code === 'Backspace' && !keyword && value.length > 0) {
                handleRemoveUser(value[value.length - 1].user_id);
              }
            }}
          />
        </div>
      }
    >
      {(close) => (
        <List
          type="option"
          className="dbo-permission-user-input__list"
          onScrollBottom={handleScrollBottom}
        >
          {renderDropdownContent(close)}
        </List>
      )}
    </Dropdown>
  );
}
